import React, { useEffect, useState, useMemo } from 'react';
import { useAppSelector } from 'src/store/hooks';
import { __DEV_OR_STAGE__ } from '@/utils/constants';
import './JwtExpirationBanner.scss';

interface JwtPayload {
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

const decodeJwt = (token: string): JwtPayload | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const formatTimeRemaining = (ms: number): string => {
  if (ms <= 0) return 'פג תוקף';
  
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

const JwtExpirationBanner: React.FC = () => {
  const accessToken = useAppSelector((state) => state.app.accessToken);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);

  const expirationTime = useMemo(() => {
    if (!accessToken) return null;
    const payload = decodeJwt(accessToken);
    if (!payload?.exp) return null;
    return payload.exp * 1000;
  }, [accessToken]);

  useEffect(() => {
    if (!expirationTime) {
      setTimeRemaining(null);
      return;
    }

    const updateTimeRemaining = () => {
      const now = Date.now();
      const remaining = expirationTime - now;
      setTimeRemaining(remaining);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [expirationTime]);

  if (!__DEV_OR_STAGE__) return null;
  if (!accessToken) return null;

  const isExpired = timeRemaining !== null && timeRemaining <= 0;
  const isWarning = timeRemaining !== null && timeRemaining > 0 && timeRemaining < 5 * 60 * 1000;

  return (
    <div
      className={`jwt-expiration-banner ${isExpired ? 'jwt-expiration-banner--expired' : ''} ${isWarning ? 'jwt-expiration-banner--warning' : ''}`}
    >
      <span className="jwt-expiration-banner__label">JWT Token:</span>
      <span className="jwt-expiration-banner__time">
        {timeRemaining !== null ? formatTimeRemaining(timeRemaining) : 'N/A'}
      </span>
    </div>
  );
};

export default JwtExpirationBanner;
